import React, { useCallback, useEffect, useRef, useState } from "react";
import { MessagesSquare, ArrowLeft, Send, X, Search } from "lucide-react";
import { api } from "../../lib/api";

const time = (s) => new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

function Thread({ who, onBack }) {
  const [rows, setRows] = useState([]);
  const [text, setText] = useState("");
  const end = useRef(null);
  const load = useCallback(() => api.get(`/team-chat/thread/${who.id}`).then(({ data }) => setRows(data)).catch(() => {}), [who.id]);
  useEffect(() => { load(); const t = setInterval(() => !document.hidden && load(), 8000); return () => clearInterval(t); }, [load]);
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [rows.length]);
  const send = async () => {
    const t = text.trim(); if (!t) return;
    setText("");
    try { const { data } = await api.post(`/team-chat/thread/${who.id}`, { text: t }); setRows((r) => [...r, data]); } catch { setText(t); }
  };
  return (
    <div className="flex flex-col h-full" data-testid="team-chat-thread">
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-slate-100">
        <button onClick={onBack} className="h-8 w-8 rounded-full hover:bg-slate-100 flex items-center justify-center" data-testid="team-chat-back"><ArrowLeft className="h-4 w-4" /></button>
        <div className="min-w-0"><div className="font-semibold text-sm truncate">{who.name}</div><div className="text-[11px] text-slate-500 truncate">{who.designation || "Team"}</div></div>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50">
        {rows.length === 0 && <div className="text-center text-xs text-slate-500 py-8">Say hello to {who.name.split(" ")[0]}.</div>}
        {rows.map((m) => {
          const mine = m.from_id !== who.id;
          return <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}><div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap break-words ${mine ? "bg-sky-600 text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"}`}>{m.text}<div className={`text-[10px] mt-0.5 ${mine ? "text-sky-100" : "text-slate-400"}`}>{time(m.created_at)}</div></div></div>;
        })}
        <div ref={end} />
      </div>
      <div className="p-2 border-t border-slate-100 flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a message" className="flex-1 h-10 rounded-full border border-slate-300 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-sky-200" data-testid="team-chat-input" />
        <button onClick={send} className="h-10 w-10 rounded-full bg-sky-600 hover:bg-sky-700 text-white flex items-center justify-center" data-testid="team-chat-send"><Send className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

function Contacts({ list, onPick }) {
  const [q, setQ] = useState("");
  const shown = list.filter((c) => (c.name || "").toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="flex flex-col h-full">
      <div className="p-2 border-b border-slate-100"><div className="relative"><Search className="h-4 w-4 text-slate-400 absolute left-3 top-3" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search team" className="w-full h-10 rounded-full border border-slate-200 pl-9 pr-3 text-sm" data-testid="team-chat-search" /></div></div>
      <div className="flex-1 overflow-y-auto divide-y divide-slate-100" data-testid="team-chat-contacts">
        {shown.map((c) => (
          <button key={c.id} onClick={() => onPick(c)} className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-slate-50 transition-colors" data-testid={`team-chat-contact-${c.id}`}>
            <div className="h-9 w-9 rounded-full bg-sky-100 text-sky-700 font-bold flex items-center justify-center shrink-0">{(c.name || "?")[0]}</div>
            <div className="flex-1 min-w-0"><div className="text-sm font-semibold truncate">{c.name}</div><div className="text-xs text-slate-500 truncate">{c.last_text || c.designation || "Tap to chat"}</div></div>
            {c.unread > 0 && <span className="h-5 min-w-[20px] px-1.5 rounded-full bg-sky-600 text-white text-[11px] font-bold flex items-center justify-center">{c.unread}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

// Direct messages between logged-in team members.
export function TeamChat() {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [list, setList] = useState([]);
  const [who, setWho] = useState(null);
  const loadContacts = useCallback(() => api.get("/team-chat/contacts").then(({ data }) => { setList(data.contacts); setUnread(data.unread); }).catch(() => {}), []);
  useEffect(() => {
    const tick = () => !document.hidden && api.get("/team-chat/unread").then(({ data }) => setUnread(data.unread)).catch(() => {});
    tick(); const t = setInterval(tick, 30000); return () => clearInterval(t);
  }, []);
  useEffect(() => { if (open && !who) loadContacts(); }, [open, who, loadContacts]);
  return (
    <div className="relative shrink-0">
      <button onClick={() => setOpen((o) => !o)} className="relative h-9 w-9 rounded-md inline-flex items-center justify-center hover:bg-sky-50 text-sky-700" title="Team chat" data-testid="team-chat-btn">
        <MessagesSquare className="h-4 w-4" />
        {unread > 0 && <span className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] px-1 rounded-full bg-sky-600 text-white text-[10px] font-bold flex items-center justify-center" data-testid="team-chat-badge">{unread > 9 ? "9+" : unread}</span>}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40 bg-black/10 sm:bg-transparent" onClick={() => setOpen(false)} />
          <div className="fixed sm:absolute inset-x-2 top-16 bottom-20 sm:inset-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-96 sm:h-[32rem] z-50 bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden flex flex-col" data-testid="team-chat-panel">
            {!who && <div className="flex items-center justify-between px-3 py-2.5 border-b border-slate-100"><div className="text-sm font-bold">Team chat</div><button onClick={() => setOpen(false)} className="h-8 w-8 rounded-full hover:bg-slate-100 flex items-center justify-center" data-testid="team-chat-close"><X className="h-4 w-4" /></button></div>}
            <div className="flex-1 min-h-0">{who ? <Thread who={who} onBack={() => { setWho(null); loadContacts(); }} /> : <Contacts list={list} onPick={setWho} />}</div>
          </div>
        </>
      )}
    </div>
  );
}
